/**
 * Borrar un producto — la confirmación.
 *
 * El borrado es real y definitivo (decisión del dueño del negocio: no quiere
 * cadáveres en el catálogo). El único freno es la auditoría: un producto con
 * ventas o con una reparación encima no se puede borrar —el backend lo
 * rechaza— y este mismo dialog ofrece el camino que sí corresponde ahí:
 * desactivarlo.
 *
 * VIVE EN CATALOGO Y NO EN RELEVAMIENTO
 * -------------------------------------
 * Nació adentro del relevamiento, como el "ya no se vende" de una tarjeta, y
 * ahí quedó encerrado: el flujo de relevamiento solo lista lo que tiene
 * `relevado_at` en null, así que cuando el recorrido terminó —334 de 334— el
 * único botón de borrar del sistema dejó de tener pantalla que lo mostrara.
 *
 * Borrar es una operación sobre el PRODUCTO, no sobre el recorrido que lo
 * encontró. Por eso ahora vive acá, lo abre la ficha, y el relevamiento lo
 * sigue usando como un invitado más.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { mensajeDeError } from "@/lib/api";
import { aNumero, type Producto } from "@/lib/tipos";

interface DialogBorrarProductoProps {
  producto: Producto | null;
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** Borra de verdad. Debe rechazar (throw) si el backend lo impide. */
  onEliminar: (producto: Producto) => Promise<void>;
  /** El plan B cuando el producto tiene ventas: `activo = false`. */
  onDesactivar: (producto: Producto) => Promise<void>;
}

export function DialogBorrarProducto({
  producto,
  abierto,
  onAbiertoChange,
  onEliminar,
  onDesactivar,
}: DialogBorrarProductoProps): JSX.Element {
  const [fase, setFase] = useState<"confirmar" | "tiene-ventas">("confirmar");
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (abierto) {
      setFase("confirmar");
      setMotivo("");
      setOcupado(false);
    }
  }, [abierto]);

  async function eliminar(): Promise<void> {
    if (!producto) return;
    setOcupado(true);
    try {
      await onEliminar(producto);
    } catch (error) {
      // El rechazo esperable es "tiene ventas": se muestra tal cual vino y se
      // ofrece la alternativa. Cualquier otro error ya lo mostró el caller.
      setMotivo(mensajeDeError(error));
      setFase("tiene-ventas");
    } finally {
      setOcupado(false);
    }
  }

  async function desactivar(): Promise<void> {
    if (!producto) return;
    setOcupado(true);
    try {
      await onDesactivar(producto);
    } catch {
      // El caller ya lo mostró; el dialog queda abierto para reintentar.
    } finally {
      setOcupado(false);
    }
  }

  /*
   * CON STOCK TAMPOCO SE DESACTIVA (0121_5). Desactivarlo sacaba esas unidades
   * del valor del stock sin un movimiento que lo dijera, que es lo mismo que
   * el borrado ya frenaba. El camino es darles salida (o ingresar lo que
   * falta) hasta dejarlo en cero; ofrecer «Desactivar» ahi era ofrecer algo
   * que la base rechaza.
   */
  const stock = producto !== null ? aNumero(producto.stock_actual) : 0;
  const conStock = stock !== 0;
  const motivoVisible = conStock ? motivoSinDesactivar(motivo, stock) : motivo;

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>
            {fase === "confirmar" ? "¿Borrar este producto?" : "No se puede borrar"}
          </DialogTitle>
          <DialogDescription>
            {fase === "confirmar"
              ? "Se borra para siempre, con sus movimientos de stock. No hay deshacer."
              : motivoVisible}
          </DialogDescription>
        </DialogHeader>

        {producto ? (
          <DialogBody>
            <Card variant="muted" padding="compacto">
              <p className="text-sm font-medium">{producto.nombre}</p>
              <p className="text-xs text-muted-foreground">
                <Codigo>{producto.codigo_interno}</Codigo>
              </p>
            </Card>
          </DialogBody>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onAbiertoChange(false)}
            disabled={ocupado}
          >
            Cancelar
          </Button>
          {fase === "confirmar" ? (
            <Button type="button" variant="destructive" cargando={ocupado} onClick={eliminar}>
              Borrar para siempre
            </Button>
          ) : conStock ? null : (
            <Button type="button" cargando={ocupado} onClick={desactivar}>
              Desactivar en su lugar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/*
 * EL MOTIVO, SIN «SE PUEDE DESACTIVAR», CUANDO HAY STOCK. eliminar_producto
 * (0125) mira las ventas, las órdenes y los repuestos ANTES que el stock, así
 * que un producto con ventas y con unidades —el caso más común— recibía «tiene
 * ventas registradas y no se puede borrar. Se puede desactivar.» y el diálogo
 * lo mostraba sin el botón, con un consejo que la base después rechaza (0121_5).
 * Se saca la sugerencia y, si el motivo no era el stock, se agrega qué falta
 * para poder desactivarlo.
 */
export function motivoSinDesactivar(motivo: string, stock: number): string {
  const limpio = motivo
    .replace(/,? o desactivalo(?=\.?$)/, "")
    .replace(/\s*Se puede desactivar\.?$/, "")
    .trim();
  if (/^Tiene -?[\d.,]+ u\. en stock/.test(limpio)) return limpio;
  const cuanto =
    stock > 0
      ? `Tiene ${stock} u. en stock: dales salida hasta dejarlo en cero`
      : `Está en ${stock} u.: ingresá lo que falta hasta dejarlo en cero`;
  return `${limpio} ${cuanto} y después se puede desactivar.`;
}
